import { createRouter, createWebHistory } from "vue-router";

import { beforeEachRoute } from "@/services/nav";
import { HANDWRITING_FLAG } from "@/plugins/handwritingMode";

// Routes
import CraneRoutes from './crane.routes'
import CheckoutRoutes from './checkout.routes'
import GateRoutes from './gate.routes'
import MeRoutes from './me.routes'

// Block Vue Router from processing hash-only popstate events on Builder.io
// pages. Must be registered BEFORE createRouter() so our listener fires
// first and can stopImmediatePropagation before Vue Router's handler runs.
window.addEventListener("popstate", (event) => {
  const meta = document.querySelector('meta[property="builderio:page"]');
  if (!meta) return;
  const builderPath = meta.getAttribute("content");
  if (window.location.pathname === builderPath) {
    event.stopImmediatePropagation();
  }
});

export const routes = [
  ...CraneRoutes,
  ...CheckoutRoutes,
  ...GateRoutes,
  ...MeRoutes,
]

const router = createRouter({
  history: createWebHistory(),
  base: import.meta.env.BASE_URL || '/',
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }

    if (savedPosition) return savedPosition

    return { x: 0, y: 0 }
  },
  routes,
});

// When in handwriting mode, keep handwriting=true in the URL so it persists on refresh
function ensureHandwritingInUrl(to, from, next) {
  if (typeof window === "undefined") {
    next();
    return;
  }
  if (window[HANDWRITING_FLAG] && to.query.handwriting !== "true") {
    next({
      path: to.path,
      query: { ...to.query, handwriting: "true" },
      replace: false,
    });
    return;
  }
  next();
}

router.beforeEach(ensureHandwritingInUrl);

// Builder.io page guard:
// On cold load the edge lambda injects <meta property="builderio:page">.
// Redirect once to BuilderioPage, then clean up meta when leaving.
let builderioActive = false;

router.beforeEach((to, from) => {
  if (builderioActive && to.name === "BuilderioPage") return;

  if (builderioActive && to.path !== from.path) {
    const meta = document.querySelector('meta[property="builderio:page"]');
    if (meta) meta.remove();
    builderioActive = false;
    return;
  }

  const meta = document.querySelector('meta[property="builderio:page"]');

  if (meta && to.name !== "BuilderioPage") {
    const urlPath = meta.getAttribute("content") || to.path;
    // Only redirect if the meta content matches the current path —
    // don't let e.g. /index.html hijack the / homepage.
    if ("/" + urlPath.replace(/^\//, "") !== to.path) return;
    builderioActive = true;
    const segments = urlPath.replace(/^\//, "").split("/").filter(Boolean);
    return {
      name: "BuilderioPage",
      params: { pathMatch: segments },
      replace: true,
    };
  }

  if (to.name === "BuilderioPage" && meta) {
    builderioActive = true;
  }
});

router.beforeEach(beforeEachRoute);

export default router
